/**
 * Searchanise Autocomplete Backdrop
 * Shows a backdrop overlay when Searchanise autocomplete dropdown is visible
 */

class SearchaniseBackdrop {
  constructor() {
    this.backdrop = null;
    this.observer = null;
    this.checkInterval = null;
    this.init();
  }

  init() {
    // Get or create backdrop element
    this.backdrop = document.querySelector('.searchanise-backdrop');
    if (!this.backdrop) {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'searchanise-backdrop';
      document.body.appendChild(this.backdrop);
    }

    // Set up click handler to close dropdown when backdrop is clicked
    this.backdrop.addEventListener('click', () => {
      this.closeSearchaniseDropdown();
    });

    // Watch for Searchanise autocomplete results
    this.observeSearchaniseResults();
  }

  /**
   * Check if Searchanise autocomplete is visible
   */
  isSearchaniseVisible() {
    const results = document.querySelector('.snize-ac-results');
    if (!results) return false;

    // Check various ways Searchanise might hide/show the dropdown
    const style = window.getComputedStyle(results);
    const isDisplayed = style.display !== 'none' && style.visibility !== 'hidden';
    const hasContent = results.children.length > 0 || results.textContent.trim().length > 0;
    const hasInlineHidden = results.style.display === 'none';

    return isDisplayed && hasContent && !hasInlineHidden;
  }

  /**
   * Update backdrop visibility based on Searchanise dropdown state
   */
  updateBackdrop() {
    if (!this.backdrop) return;

    const isVisible = this.isSearchaniseVisible();
    
    if (isVisible) {
      this.backdrop.classList.add('active');
    } else {
      this.backdrop.classList.remove('active');
    }
  }

  /**
   * Observe Searchanise results element for changes
   */
  observeSearchaniseResults() {
    // Use MutationObserver to watch for changes
    this.observer = new MutationObserver(() => {
      this.updateBackdrop();
    });

    // Try to find the results element immediately
    const results = document.querySelector('.snize-ac-results');
    if (results) {
      this.observer.observe(results, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      this.updateBackdrop();
    }

    // Also watch for when Searchanise injects the element
    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if this is the results element or contains it
            const results = node.classList?.contains('snize-ac-results')
              ? node
              : node.querySelector?.('.snize-ac-results');
            
            if (results) {
              this.observer.observe(results, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
              });
              this.updateBackdrop();
            }
          }
        }
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Fallback: periodic check (in case MutationObserver misses something)
    this.checkInterval = setInterval(() => {
      this.updateBackdrop();
    }, 200);
  }

  /**
   * Close Searchanise dropdown by blurring the input
   */
  closeSearchaniseDropdown() {
    // Find and blur the Searchanise input
    const selectors = [
      '.snize-input-style',
      '.snize-autocomplete-input',
      '.snize-ac-input',
      'input[name="q"].snize-input-style',
      'input[class*="snize"]'
    ];

    for (const selector of selectors) {
      const input = document.querySelector(selector);
      if (input && document.activeElement === input) {
        input.blur();
        break;
      }
    }

    // Also try to hide the results directly
    const results = document.querySelector('.snize-ac-results');
    if (results) {
      results.style.display = 'none';
    }
  }

  /**
   * Clean up
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.searchaniseBackdrop = new SearchaniseBackdrop();
  });
} else {
  window.searchaniseBackdrop = new SearchaniseBackdrop();
}

